import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  MenuItem
} from '@mui/material';
import { Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import EditOverrideModal from './components/EditOverrideModal';
import { API_BASE_URL } from '../config/config';
import {
  getDisplayBaggage,
  getDisplayGate,
  getBaggageSource,
  getGateSource
} from '../types/flight';

/**
 * BaggageGateEditor
 * Admin page for managing manual overrides for baggage (arrivals) and gates (departures)
 */
const BaggageGateEditor = () => {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useState({
    flightNumber: '',
    from: '',
    to: '',
    origin: '',
    destination: '',
    direction: ''
  });
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Load flights on component mount
  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Get auth token from localStorage
      const authData = JSON.parse(localStorage.getItem('auth') || '{}');
      const token = authData.token;

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Build query string
      const params = new URLSearchParams();
      if (searchParams.flightNumber) params.append('flightNumber', searchParams.flightNumber);
      if (searchParams.from) params.append('from', new Date(searchParams.from).toISOString());
      if (searchParams.to) params.append('to', new Date(searchParams.to).toISOString());
      if (searchParams.origin) params.append('origin', searchParams.origin);
      if (searchParams.destination) params.append('destination', searchParams.destination);
      if (searchParams.direction) params.append('direction', searchParams.direction);

      // Make API request
      const response = await fetch(`${API_BASE_URL}/api/flights?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to fetch flights: ${response.status}`);
      }

      setFlights(data.data || []);

    } catch (err) {
      console.error('Error fetching flights:', err);
      setError(err.message || 'Failed to fetch flights. Please try again.');
      setFlights([]);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditClick = (flight) => {
    setSelectedFlight(flight);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedFlight(null);
  };

  const handleUpdateSuccess = (updatedFlight) => {
    // Update the flight in the list
    setFlights(prevFlights =>
      prevFlights.map(f =>
        f._id === updatedFlight._id
          ? { ...f, ...updatedFlight }
          : f
      )
    );

    // Show success message
    setError('');
  };

  const handleReset = () => {
    setSearchParams({
      flightNumber: '',
      from: '',
      to: '',
      origin: '',
      destination: '',
      direction: ''
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#000', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600, color: '#fff' }}>
        Baggage & Gate Editor
      </Typography>

      {/* Search Filters */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#1a1a1a' }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#fff' }}>
          Search Filters
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Flight Number"
              placeholder="e.g., AC123"
              value={searchParams.flightNumber}
              onChange={(e) => handleInputChange('flightNumber', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#666' }, '&:hover fieldset': { borderColor: '#999' } },
                '& .MuiInputLabel-root': { color: '#999' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#fff' }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Type"
              value={searchParams.direction}
              onChange={(e) => handleInputChange('direction', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#666' }, '&:hover fieldset': { borderColor: '#999' } },
                '& .MuiInputLabel-root': { color: '#999' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#fff' }
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="A">Arrivals</MenuItem>
              <MenuItem value="D">Departures</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Origin"
              placeholder="e.g., YUL"
              value={searchParams.origin}
              onChange={(e) => handleInputChange('origin', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#666' }, '&:hover fieldset': { borderColor: '#999' } },
                '& .MuiInputLabel-root': { color: '#999' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#fff' }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Destination"
              placeholder="e.g., YYZ"
              value={searchParams.destination}
              onChange={(e) => handleInputChange('destination', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#666' }, '&:hover fieldset': { borderColor: '#999' } },
                '& .MuiInputLabel-root': { color: '#999' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#fff' }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="datetime-local"
              label="From"
              value={searchParams.from}
              onChange={(e) => handleInputChange('from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#666' }, '&:hover fieldset': { borderColor: '#999' } },
                '& .MuiInputLabel-root': { color: '#999' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#fff' }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="datetime-local"
              label="To"
              value={searchParams.to}
              onChange={(e) => handleInputChange('to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#666' }, '&:hover fieldset': { borderColor: '#999' } },
                '& .MuiInputLabel-root': { color: '#999' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#fff' }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              sx={{ height: '40px' }}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
              sx={{ height: '40px' }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Table */}
      <Paper sx={{ backgroundColor: '#1a1a1a' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #333' }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Flight Results ({flights.length})
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#2a2a2a' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Flight</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Scheduled</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Origin</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Destination</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Gate</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Baggage</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && flights.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && flights.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#fff' }}>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      No flights found. Adjust your search filters and try again.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {flights.map((flight, index) => {
                const gateValue = getDisplayGate(flight);
                const gateSource = getGateSource(flight);
                const baggageValue = getDisplayBaggage(flight);
                const baggageSource = getBaggageSource(flight);

                return (
                  <TableRow key={flight._id} hover sx={{ backgroundColor: index % 2 === 0 ? '#1a1a1a' : '#000', '&:hover': { backgroundColor: '#2a2a2a' } }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#fff' }}>
                        {flight.airline}{flight.flightNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Chip
                        label={flight.direction === 'A' ? 'Arrival' : 'Departure'}
                        size="small"
                        color={flight.direction === 'A' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {formatTime(flight.scheduledTime)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {flight.route?.origin || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {flight.route?.destination || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {gateValue ? (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>{gateValue}</Typography>
                          {gateSource && (
                            <Chip
                              label={gateSource}
                              size="small"
                              variant="outlined"
                              color={gateSource === 'MANUAL' ? 'success' : 'default'}
                              sx={{ mt: 0.5, color: '#fff', borderColor: gateSource === 'MANUAL' ? '#4caf50' : '#999' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#999', fontWeight: 'bold' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {baggageValue ? (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>{baggageValue}</Typography>
                          {baggageSource && (
                            <Chip
                              label={baggageSource}
                              size="small"
                              variant="outlined"
                              color={baggageSource === 'MANUAL' ? 'success' : 'default'}
                              sx={{ mt: 0.5, color: '#fff', borderColor: baggageSource === 'MANUAL' ? '#4caf50' : '#999' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#999', fontWeight: 'bold' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(flight)}
                        sx={{ color: '#fff' }}
                        title="Edit Override"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Modal */}
      <EditOverrideModal
        open={modalOpen}
        onClose={handleModalClose}
        flight={selectedFlight}
        onSuccess={handleUpdateSuccess}
      />
    </Box>
  );
};

export default BaggageGateEditor;
